import React, { useEffect, useState, useCallback, useRef } from 'react';

export default function BarHittingGame({ open, onClose }) {
    const [ballPosition, setBallPosition] = useState({ x: 50, y: 65 }); // Start at FIFA penalty spot
    const [ballFlying, setBallFlying] = useState(false);
    const [gameResult, setGameResult] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [score, setScore] = useState(0);
    const animationFrameRef = useRef();

    const resetGame = useCallback(() => {
        setBallPosition({ x: 50, y: 65 }); // Reset to FIFA penalty spot
        setBallFlying(false);
        setGameResult(null);
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }
    }, []);

    const startNewGame = useCallback(() => {
        setAttempts(0);
        setScore(0);
        resetGame();
    }, [resetGame]);

    useEffect(() => {
        if (open) {
            startNewGame();
        }
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [open, startNewGame]);

    const hitBall = useCallback(() => {
        if (ballFlying) return;

        setBallFlying(true);
        
        // Determine if this shot will hit (20% chance)
        const willHit = Math.random() < 0.2;
        
        // Calculate trajectory with random power variation for realism
        const randomPower = 0.6 + Math.random() * 0.4; // Random power between 60-100%
        const maxHeight = 15 + (randomPower * 20); // Trajectory height based on random power
        
        let targetX;
        let targetY;
        
        if (willHit) {
            // Hit the crossbar (top bar) - positioned at the goal (y: 10-12%)
            targetX = 48 + Math.random() * 4; // Small variation for realism  
            targetY = 10 + Math.random() * 2; // Target the crossbar area (10-12%)
        } else {
            // Miss - various miss patterns that AVOID the crossbar
            const missType = Math.random();
            if (missType < 0.4) {
                // Miss over the goal (too high) - well above crossbar
                targetX = 45 + Math.random() * 10;
                targetY = 3 + Math.random() * 5; // Well above the crossbar (3-8%)
            } else if (missType < 0.7) {
                // Miss to the sides
                const side = Math.random() < 0.5 ? -1 : 1;
                targetX = 50 + side * (25 + Math.random() * 20);
                targetY = 8 + Math.random() * 25; // Various heights but avoid crossbar zone
            } else {
                // Miss under (too low - below crossbar into net or ground)
                targetX = 45 + Math.random() * 10;
                targetY = 15 + Math.random() * 25; // Well below crossbar (15%+)
            }
        }

        // Animate ball trajectory
        const startTime = Date.now();
        const duration = 800 + (randomPower * 400); // Duration based on random power
        const startX = ballPosition.x;
        const startY = ballPosition.y;

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // More realistic trajectory - ball goes toward the goal
            const x = startX + (targetX - startX) * progress;
            // Modified trajectory to go toward the goal instead of up first
            const heightProgress = Math.sin(progress * Math.PI); // Sine wave for natural arc
            const y = startY + (targetY - startY) * progress - (maxHeight * heightProgress);
            
            setBallPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
            
            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            } else {
                // Animation complete
                setBallFlying(false);
                setAttempts(prev => prev + 1);
                
                if (willHit) {
                    setScore(prev => prev + 1);
                    setGameResult('hit');
                } else {
                    setGameResult('miss');
                }
                
                // Reset for next attempt after showing result
                setTimeout(() => {
                    resetGame();
                }, 2000);
            }
        };
        
        animate();
    }, [ballFlying, ballPosition, resetGame]);

    if (!open) return null;

    return (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            {/* Game Container - Centered */}
            <div className="relative w-full max-w-5xl h-[80vh] bg-gradient-to-b from-sky-500 via-sky-400 to-green-600 rounded-xl shadow-2xl border border-white/20 overflow-hidden">
                
                {/* Header */}
                <div className="relative flex items-center justify-between px-4 sm:px-6 py-4 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-b border-slate-700 shadow-lg">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-lg">
                                <span className="text-2xl">⚽</span>
                            </div>
                            <div>
                                <h1 className="font-bold text-white text-lg sm:text-xl">
                                    Penalty Crossbar Challenge
                                </h1>
                                <p className="text-sm text-slate-300">Hit the crossbar from the penalty spot!</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/80 border border-slate-600">
                            <span className="text-slate-300 text-xs font-medium">Attempts</span>
                            <span className="font-bold text-blue-400">{attempts}</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/80 border border-slate-600">
                            <span className="text-slate-300 text-xs font-medium">Hits</span>
                            <span className="font-bold text-emerald-400">{score}</span>
                        </div>
                        <button
                            type="button"
                            onClick={startNewGame}
                            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all duration-200 hover:scale-105 shadow-lg border border-blue-500"
                        >
                            🔄 New Game
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all duration-200 hover:scale-105 shadow-lg border border-red-500"
                        >
                            ✕ Close
                        </button>
                    </div>
                </div>

                {/* Game Area */}
                <div className="relative" style={{ height: 'calc(100% - 80px)' }}>
                    {/* Perspective gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-green-700/40 via-green-500/20 to-transparent pointer-events-none"></div>
                    {/* Penalty Area Markings */}
                    <div className="absolute inset-0">
                        {/* Goal Area (6-yard box) - FIFA proportions: 18.29m × 5.49m */}
                        <div 
                            className="absolute border-4 border-white"
                            style={{
                                top: '32px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '480px', // Goal is 320px, so goal area should be 480px (18.29m vs 7.32m = 2.5x)
                                height: '140px', // 5.49m depth scaled proportionally
                                borderBottom: 'none',
                                boxShadow: '0 0 8px rgba(255,255,255,0.6), inset 0 0 8px rgba(255,255,255,0.3)'
                            }}
                        ></div>
                        
                        {/* Penalty Area (18-yard box) - FIFA proportions: 40.32m × 16.5m */}
                        <div 
                            className="absolute border-4 border-white"
                            style={{
                                top: '32px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '700px', // 40.32m vs 7.32m = 5.5x goal width
                                height: '420px', // 16.5m depth scaled proportionally  
                                borderBottom: 'none',
                                boxShadow: '0 0 8px rgba(255,255,255,0.6), inset 0 0 8px rgba(255,255,255,0.3)'
                            }}
                        ></div>
                        
                        {/* Penalty Arc - FIFA proportions: 9.15m radius */}
                        <div 
                            className="absolute border-4 border-white"
                            style={{
                                top: '452px', // Positioned at edge of penalty area
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '240px', // 9.15m radius × 2 = 18.3m diameter
                                height: '120px', // Half circle
                                borderRadius: '0 0 120px 120px',
                                borderTop: 'none',
                                boxShadow: '0 0 8px rgba(255,255,255,0.6)'
                            }}
                        ></div>
                        
                        {/* Center line (partial) - Enhanced */}
                        <div 
                            className="absolute bg-white"
                            style={{
                                bottom: '60px',
                                left: '0',
                                right: '0',
                                height: '4px',
                                boxShadow: '0 0 6px rgba(255,255,255,0.7)'
                            }}
                        ></div>
                        
                        {/* Penalty Spot - FIFA position: 11m from goal line */}
                        <div 
                            className="absolute bg-white rounded-full border-2 border-gray-200"
                            style={{
                                top: '310px', // 11m from goal line (proportional to penalty area depth)
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '12px',
                                height: '12px',
                                boxShadow: '0 0 8px rgba(255,255,255,0.8), 0 2px 4px rgba(0,0,0,0.3)'
                            }}
                        ></div>
                        
                        {/* Corner Flag Areas - Aligned with goal */}
                        <div 
                            className="absolute"
                            style={{
                                top: '32px',
                                left: '8px',
                                width: '30px',
                                height: '30px',
                                border: '2px solid rgba(255,255,255,0.6)',
                                borderRadius: '0 0 30px 0',
                                borderTop: 'none',
                                borderLeft: 'none'
                            }}
                        ></div>
                        <div 
                            className="absolute"
                            style={{
                                top: '32px',
                                right: '8px',
                                width: '30px',
                                height: '30px',
                                border: '2px solid rgba(255,255,255,0.6)',
                                borderRadius: '0 0 0 30px',
                                borderTop: 'none',
                                borderRight: 'none'
                            }}
                        ></div>
                        
                        {/* Side Lines - Enhanced */}
                        <div 
                            className="absolute bg-white"
                            style={{
                                top: '32px',
                                left: '0',
                                width: '4px',
                                height: '300px',
                                boxShadow: '0 0 6px rgba(255,255,255,0.6)'
                            }}
                        ></div>
                        <div 
                            className="absolute bg-white"
                            style={{
                                top: '32px',
                                right: '0',
                                width: '4px',
                                height: '300px',
                                boxShadow: '0 0 6px rgba(255,255,255,0.6)'
                            }}
                        ></div>
                        
                        {/* Goal Line - Enhanced with prominent glow */}
                        <div 
                            className="absolute bg-white"
                            style={{
                                top: '32px',
                                left: '0',
                                right: '0',
                                height: '4px',
                                boxShadow: '0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.4)'
                            }}
                        ></div>
                    </div>

                    {/* Goal Shadow - positioned behind goal line */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-80 h-8 bg-black/20 rounded-full blur-sm" style={{ top: '40px' }}></div>
                    
                    {/* Soccer Goal - BEHIND the goal line */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-80 h-40" style={{ top: '40px' }}>
                        {/* Left Goal Post - Enhanced with depth */}
                        <div className="absolute left-0 w-6 h-36 bg-gradient-to-r from-gray-100 via-white to-gray-200 rounded-t-lg shadow-2xl border border-gray-300" style={{ bottom: '-8px' }}>
                            <div className="absolute right-0 top-0 w-1 h-full bg-gray-400/50 rounded-r-lg"></div>
                        </div>
                        
                        {/* Right Goal Post - Enhanced with depth */}
                        <div className="absolute right-0 w-6 h-36 bg-gradient-to-l from-gray-100 via-white to-gray-200 rounded-t-lg shadow-2xl border border-gray-300" style={{ bottom: '-8px' }}>
                            <div className="absolute left-0 top-0 w-1 h-full bg-gray-400/50 rounded-l-lg"></div>
                        </div>
                        
                        {/* Crossbar (Top Bar) - Enhanced with shading */}
                        <div 
                            className="absolute left-0 right-0 w-full h-6 bg-gradient-to-b from-gray-100 via-white to-gray-200 rounded-lg shadow-2xl border border-gray-300"
                            style={{ top: '0px' }}
                        >
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-400/50 rounded-b-lg"></div>
                        </div>
                        
                        {/* Goal Base Frame */}
                        <div 
                            className="absolute left-0 right-0 w-full h-3 bg-gradient-to-b from-gray-200 to-gray-300 shadow-lg border-t border-gray-300"
                            style={{ bottom: '-8px' }}
                        ></div>
                        
                        {/* Clean Soccer Net - Simple grid pattern */}
                        <div 
                            className="absolute"
                            style={{ 
                                left: '24px',
                                top: '6px',
                                width: 'calc(100% - 48px)',
                                height: 'calc(100% - 14px)',
                            }}
                        >
                            {/* Simple grid net pattern */}
                            <svg 
                                className="w-full h-full"
                                style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))' }}
                                viewBox="0 0 232 122"
                            >
                                {/* Vertical net lines */}
                                {Array.from({ length: 12 }).map((_, i) => {
                                    const x = i * 20 + 10;
                                    const depth = i / 12; // 0 to 1 for perspective
                                    const endX = x + (depth * 15); // Perspective shift
                                    return (
                                        <line
                                            key={`net-v-${i}`}
                                            x1={x}
                                            y1="0"
                                            x2={endX}
                                            y2="122"
                                            stroke="rgba(255,255,255,0.7)"
                                            strokeWidth="1"
                                        />
                                    );
                                })}
                                
                                {/* Horizontal net lines */}
                                {Array.from({ length: 8 }).map((_, i) => {
                                    const y = i * 15 + 5;
                                    const depth = i / 8; // 0 to 1 for perspective
                                    return (
                                        <line
                                            key={`net-h-${i}`}
                                            x1="10"
                                            y1={y}
                                            x2={222 + (depth * 10)}
                                            y2={y + (depth * 5)}
                                            stroke="rgba(255,255,255,0.6)"
                                            strokeWidth="1"
                                        />
                                    );
                                })}
                                
                                {/* Diagonal support lines for realism */}
                                {Array.from({ length: 6 }).map((_, i) => {
                                    const x = i * 40 + 20;
                                    return (
                                        <line
                                            key={`net-d-${i}`}
                                            x1={x}
                                            y1="0"
                                            x2={x + 20}
                                            y2="60"
                                            stroke="rgba(255,255,255,0.4)"
                                            strokeWidth="0.8"
                                        />
                                    );
                                })}
                            </svg>
                        </div>
                    </div>

                    {/* Soccer Ball */}
                    <div 
                        className="absolute w-10 h-10 bg-white rounded-full shadow-lg border border-gray-300 transition-all duration-100 cursor-pointer hover:scale-110"
                        style={{
                            left: `${ballPosition.x}%`,
                            top: `${ballPosition.y}%`,
                            transform: 'translate(-50%, -50%)',
                            filter: 'drop-shadow(3px 3px 6px rgba(0,0,0,0.4))',
                            zIndex: 10
                        }}
                        onClick={hitBall}
                    >
                        {/* Classic Soccer Ball Pattern */}
                        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 40 40">
                            <defs>
                                <pattern id="soccerPattern" patternUnits="objectBoundingBox" width="1" height="1">
                                    <circle cx="20" cy="20" r="18" fill="white" stroke="#ddd" strokeWidth="0.5"/>
                                </pattern>
                            </defs>
                            
                            {/* Main white circle */}
                            <circle cx="20" cy="20" r="18" fill="white" stroke="#ccc" strokeWidth="0.5"/>
                            
                            {/* Classic soccer ball black pattern */}
                            <g fill="black">
                                {/* Central pentagon */}
                                <path d="M 20,12 L 16,16 L 18,23 L 22,23 L 24,16 Z" />
                                
                                {/* Curved connecting lines (hexagon edges) */}
                                <path d="M 20,12 Q 15,8 10,12 Q 8,15 10,18 L 16,16" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M 24,16 Q 28,12 32,15 Q 32,18 30,20 Q 26,22 22,23" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M 18,23 Q 15,28 12,30 Q 15,32 18,30 Q 20,28 20,25" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M 22,23 Q 25,28 28,30 Q 25,32 22,30 Q 20,28 20,25" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                                <path d="M 16,16 Q 12,18 8,20 Q 8,22 10,24 Q 14,25 18,23" fill="none" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                                
                                {/* Additional curved lines for realism */}
                                <path d="M 20,12 Q 25,8 30,12" fill="none" stroke="black" strokeWidth="1" strokeLinecap="round"/>
                                <path d="M 10,18 Q 8,22 10,26" fill="none" stroke="black" strokeWidth="1" strokeLinecap="round"/>
                                <path d="M 30,20 Q 32,22 30,26" fill="none" stroke="black" strokeWidth="1" strokeLinecap="round"/>
                            </g>
                        </svg>
                    </div>

                    {/* Game Instructions */}
                    {!ballFlying && (
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black/80 backdrop-blur-sm rounded-xl p-4 border border-white/30 shadow-2xl">
                            <div className="text-center">
                                <div className="text-white font-bold text-lg mb-2">⚽ Penalty Kick</div>
                                <div className="text-white/90 text-sm">Click the ball to take the penalty!</div>
                                <div className="text-white/70 text-xs mt-1">Challenge: Hit the crossbar</div>
                            </div>
                        </div>
                    )}

                    {/* Result Display */}
                    {gameResult && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                            <div className="bg-black/90 backdrop-blur-md rounded-3xl p-8 border border-white/30 text-center max-w-md mx-4 shadow-2xl">
                                {gameResult === 'hit' ? (
                                    <>
                                        <div className="text-6xl mb-4">🎯</div>
                                        <div className="text-3xl font-bold text-green-400 mb-2">CROSSBAR HIT!</div>
                                        <div className="text-white/90 mb-4">
                                            Perfect shot! You hit the crossbar!
                                        </div>
                                        <div className="text-emerald-300 font-semibold">
                                            Challenge completed! 🏆
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-6xl mb-4">😔</div>
                                        <div className="text-3xl font-bold text-red-400 mb-2">Missed!</div>
                                        <div className="text-white/90 mb-4">
                                            Aim for the crossbar! Try adjusting your power.
                                        </div>
                                        <div className="text-blue-300 font-semibold">
                                            Better luck next shot! ⚽
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}


                    {/* Ground */}
                    <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-green-500 via-green-400 to-green-500"></div>
                </div>
            </div>
        </div>
    );
}